import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import {
  Wrench, ShieldCheck, Lightning, Star, ArrowRight, CheckCircle, Phone,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import ServicesGrid from "@/components/ServicesGrid";
import TestimonialsSection from "@/components/TestimonialsSection";

function Counter({ to, suffix = "", duration = 1.6 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(to * eased));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, to, duration]);
  return (
    <span ref={ref} data-testid="counter">{val}{suffix}</span>
  );
}

export default function HomePage() {
  return (
    <>
      <SEO
        title="MECA AUTO 17 — Garage Automobile Tonnay-Charente | Entretien & Réparation"
        description="MECA AUTO 17, votre garage automobile à Tonnay-Charente (17430). Entretien, révision, diagnostic et réparation toutes marques. Devis gratuit et rendez-vous rapide."
      />

      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[90vh] flex items-center pt-20 overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/33814736/pexels-photo-33814736.jpeg"
            alt="Atelier mécanique moderne"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0D2C54]/90 via-[#0D2C54]/70 to-[#0D2C54]/30" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 py-20 w-full">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-6 text-white/80">
              <div className="w-12 h-px bg-[#E63946]" />
              <span className="overline text-xs">Garage automobile – Tonnay-Charente 17430</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight">
              Votre garage <br />
              <span className="text-[#E63946]">automobile</span> de confiance.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed">
              Entretien, réparation et diagnostic toutes marques par des techniciens qualifiés en Charente-Maritime. Devis transparent, intervention rapide, satisfaction garantie.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/rendez-vous">
                <Button data-testid="hero-rdv-btn" className="bg-[#E63946] hover:bg-[#D62839] text-white rounded-none h-14 px-8 font-semibold text-base group">
                  Prendre rendez-vous
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} weight="bold" />
                </Button>
              </Link>
              <Link to="/devis">
                <Button data-testid="hero-devis-btn" variant="outline" className="rounded-none h-14 px-8 font-semibold text-base bg-transparent border-white/40 text-white hover:bg-white hover:text-[#0D2C54]">
                  Demander un devis gratuit
                </Button>
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl">
              {[
                { label: "Toutes marques" },
                { label: "Devis gratuit" },
                { label: "Garantie pièces" },
                { label: "Intervention 24h" },
              ].map((b) => (
                <div key={b.label} className="flex items-center gap-2 text-white/90 text-sm">
                  <CheckCircle size={18} className="text-[#E63946] shrink-0" weight="fill" />
                  <span>{b.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom band: phone */}
        <div className="absolute bottom-0 left-0 right-0 bg-[#0D2C54] border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-4 flex flex-col md:flex-row items-center justify-between gap-3 text-white text-sm">
            <div className="flex items-center gap-3"><Phone size={18} className="text-[#E63946]" weight="bold" />Une question ? Appelez-nous au <a href="tel:+33546887088" className="font-semibold hover:text-[#E63946]">05 46 88 70 88</a></div>
            <div className="overline text-xs text-white/60">Lun–Ven 8h–18h30 · Sam 9h–17h</div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section data-testid="why-us-section" className="py-20 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-12 gap-12 items-end mb-16">
            <div className="md:col-span-7">
              <span className="overline text-[#E63946]">Pourquoi nous choisir</span>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0D2C54] tracking-tight">
                L'expertise mécanique, sans compromis.
              </h2>
            </div>
            <p className="md:col-span-5 text-[#555555] text-base leading-relaxed">
              Depuis plus de 10 ans, notre garage met son savoir-faire au service des automobilistes de Tonnay-Charente, Rochefort et de toute la Charente-Maritime. Diagnostic précis, transparence et qualité d'exécution sont nos engagements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-[#E5E7EB]">
            {[
              { icon: Wrench, title: "Expertise toutes marques", desc: "Équipements multimarques pour intervenir sur tous les véhicules essence, diesel, hybrides et électriques." },
              { icon: ShieldCheck, title: "Techniciens qualifiés", desc: "Formations constructeur régulières et certifications professionnelles à jour." },
              { icon: Star, title: "Satisfaction garantie", desc: "98% de clients satisfaits. Garantie pièces et main d'œuvre sur toutes nos interventions." },
              { icon: Lightning, title: "Intervention rapide", desc: "Délai moyen d'intervention de 24h. Véhicule de prêt disponible sur demande." },
            ].map((f, i) => (
              <div
                key={i}
                data-testid={`why-item-${i}`}
                className="border-r border-b border-[#E5E7EB] p-8 md:p-10 group hover:bg-[#F8F9FA] transition-colors"
              >
                <f.icon size={36} className="text-[#E63946]" weight="duotone" />
                <h3 className="mt-6 font-display font-semibold text-xl text-[#0D2C54]">{f.title}</h3>
                <p className="mt-3 text-[#555555] text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <ServicesGrid />

      {/* STATS */}
      <section data-testid="stats-section" className="bg-[#0D2C54] text-white py-20 md:py-28 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-16">
            <span className="overline text-[#E63946]">En chiffres</span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              La confiance, mesurée.
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4">
            {[
              { v: 10, s: "+", label: "Années d'expérience" },
              { v: 5000, s: "+", label: "Véhicules réparés" },
              { v: 98, s: "%", label: "Clients satisfaits" },
              { v: 24, s: "h", label: "Délai moyen" },
            ].map((it, i) => (
              <div key={i} className="text-center px-4 border-r border-white/10 last:border-r-0">
                <div className="font-display font-bold text-5xl md:text-6xl lg:text-7xl text-[#E63946]">
                  <Counter to={it.v} suffix={it.s} />
                </div>
                <div className="mt-3 overline text-xs text-white/70">{it.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <TestimonialsSection />

      {/* CTA */}
      <section data-testid="bottom-cta" className="py-20 md:py-28 px-6 md:px-12 bg-[#F8F9FA]">
        <div className="max-w-[1100px] mx-auto text-center">
          <span className="overline text-[#E63946]">Prêt à confier votre véhicule ?</span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0D2C54] tracking-tight">
            Réservez votre intervention en moins d'une minute.
          </h2>
          <p className="mt-6 text-[#555555] text-lg max-w-2xl mx-auto">
            Choisissez votre service, votre créneau et laissez-nous nous occuper du reste.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/rendez-vous">
              <Button data-testid="cta-rdv-btn" className="bg-[#E63946] hover:bg-[#D62839] text-white rounded-none h-14 px-8 font-semibold">Prendre rendez-vous</Button>
            </Link>
            <Link to="/devis">
              <Button data-testid="cta-devis-btn" variant="outline" className="rounded-none h-14 px-8 font-semibold border-[#0D2C54] text-[#0D2C54] hover:bg-[#0D2C54] hover:text-white">Demander un devis gratuit</Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
